import React, { FC, useEffect, useState, useRef } from 'react';
import { TextField } from '@mui/material';

interface COETrainingDurationInputProps {
  value: string | number;
  onChange: (seconds: number) => void;
}

const formatTimeComponent = (component: number) => component.toString().padStart(2, '0');

const secondsToHHMMSS = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  const formattedHours = formatTimeComponent(hours);
  const formattedMinutes = formatTimeComponent(minutes);
  const formattedSeconds = formatTimeComponent(secs);

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

const parseDurationToSeconds = (input: string): number => {
  const raw = (input || '').trim();
  if (!raw) return 0;

  // Allow only digits and colons, collapse multiple colons to at most two
  const cleaned = raw.replace(/[^\d:]/g, '');
  const parts = cleaned.split(':').filter(Boolean);

  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (parts.length === 3) {
    hours = parseInt(parts[0], 10) || 0;
    minutes = parseInt(parts[1], 10) || 0;
    seconds = parseInt(parts[2], 10) || 0;
  } else if (parts.length === 2) {
    hours = parseInt(parts[0], 10) || 0;
    minutes = parseInt(parts[1], 10) || 0;
  } else if (parts.length === 1) {
    const digits = parts[0];
    const len = digits.length;
    if (len <= 2) {
      // Treat as seconds
      seconds = parseInt(digits, 10) || 0;
    } else if (len <= 4) {
      // MMSS – last two are seconds, rest minutes
      const mins = digits.slice(0, -2);
      const secs = digits.slice(-2);
      minutes = parseInt(mins, 10) || 0;
      seconds = parseInt(secs, 10) || 0;
    } else {
      // HHMMSS – last two seconds, previous two minutes, rest hours
      const hrs = digits.slice(0, -4);
      const mins = digits.slice(-4, -2);
      const secs = digits.slice(-2);
      hours = parseInt(hrs, 10) || 0;
      minutes = parseInt(mins, 10) || 0;
      seconds = parseInt(secs, 10) || 0;
    }
  } else {
    return 0;
  }

  // Clamp minutes/seconds to sensible ranges
  if (minutes > 59) minutes = 59;
  if (seconds > 59) seconds = 59;

  return hours * 3600 + minutes * 60 + seconds;
};

const COETrainingDurationInput: FC<COETrainingDurationInputProps> = ({ value, onChange }) => {
  const initialSeconds = typeof value === 'string' ? parseInt(value, 10) || 0 : value || 0;
  const [internalValue, setInternalValue] = useState<string>(secondsToHHMMSS(initialSeconds));
  const [isFocused, setIsFocused] = useState(false);
  const lastSecondsRef = useRef<number>(initialSeconds);

  useEffect(() => {
    if (isFocused) return;
    const propSeconds = typeof value === 'string' ? parseInt(value, 10) || 0 : value || 0;
    if (!Number.isFinite(propSeconds)) return;

    const currentInternalSeconds = parseDurationToSeconds(internalValue);
    if (propSeconds !== currentInternalSeconds) {
      setInternalValue(secondsToHHMMSS(propSeconds));
      lastSecondsRef.current = propSeconds;
    }
  }, [value, isFocused, internalValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow only digits and colons
    let cleaned = raw.replace(/[^\d:]/g, '');
    // At most two colons
    const colonParts = cleaned.split(':');
    if (colonParts.length > 3) {
      cleaned = colonParts.slice(0, 3).join(':');
    }

    setInternalValue(cleaned);

    const seconds = parseDurationToSeconds(cleaned);
    if (seconds !== lastSecondsRef.current) {
      lastSecondsRef.current = seconds;
      onChange(seconds);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const seconds = parseDurationToSeconds(internalValue);
    const normalized = secondsToHHMMSS(seconds);
    setInternalValue(normalized);
    if (seconds !== lastSecondsRef.current) {
      lastSecondsRef.current = seconds;
      onChange(seconds);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    e.target.select();
  };

  return (
    <TextField
      size="small"
      value={internalValue}
      placeholder="00:00:00"
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};

export default COETrainingDurationInput;
